import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "placeholder",
    "previewContainer",
    "preview",
    "recordButton",
    "stopButton",
    "retakeButton",
    "uploadButton",
    "recordingIndicator",
    "timer",
    "blobInput",
    "existingVideo",
      "actionButtons",
      "deleteButtonContainer"
  ]

  connect() {
    this.mediaRecorder = null
    this.recordedChunks = []
    this.stream = null
    this.timerInterval = null
    this.recordingTime = 0
    this.maxDuration = 30 // 30 seconds max
  }

  disconnect() {
    this.cleanup()
  }

    /**
     * Check if running inside Flutter WebView
     */
    get isInFlutterApp() {
        return typeof window.flutter_inappwebview !== "undefined"
    }

  stopRecording() {
    if (this.mediaRecorder && this.mediaRecorder.state === "recording") {
      this.mediaRecorder.stop()
    }
    this.stopTimer()
    this.stopStream()

    // Update UI - hide stop button and recording indicator
    if (this.hasStopButtonTarget) {
      this.stopButtonTarget.classList.add("hidden")
        this.stopButtonTarget.classList.remove("inline-flex")
    }
    if (this.hasRecordingIndicatorTarget) {
      this.recordingIndicatorTarget.classList.add("hidden")
      this.recordingIndicatorTarget.classList.remove("flex")
    }
  }

  handleRecordingComplete() {
    const blob = new Blob(this.recordedChunks, { type: this.getSupportedMimeType() })
    const videoURL = URL.createObjectURL(blob)

      // Hide placeholder and existing video if present
      if (this.hasPlaceholderTarget) {
          this.placeholderTarget.classList.add("hidden")
      }
      if (this.hasExistingVideoTarget) {
          this.existingVideoTarget.classList.add("hidden")
      }

    // Show preview of recorded video
    if (this.hasPreviewTarget) {
      this.previewTarget.srcObject = null
      this.previewTarget.src = videoURL
      this.previewTarget.muted = false
      this.previewTarget.controls = true
    }
      if (this.hasPreviewContainerTarget) {
          this.previewContainerTarget.classList.remove("hidden")
      }

      // Show delete button container (for newly recorded video)
      if (this.hasDeleteButtonContainerTarget) {
          this.deleteButtonContainerTarget.classList.remove("hidden")
      }

    // Show retake button, hide record and upload buttons
    if (this.hasRetakeButtonTarget) {
      this.retakeButtonTarget.classList.remove("hidden")
        this.retakeButtonTarget.classList.add("inline-flex")
    }
    if (this.hasRecordButtonTarget) {
      this.recordButtonTarget.classList.add("hidden")
        this.recordButtonTarget.classList.remove("inline-flex")
    }
    if (this.hasUploadButtonTarget) {
      this.uploadButtonTarget.classList.add("hidden")
        this.uploadButtonTarget.classList.remove("inline-flex")
    }

    // Convert blob to base64 and store for form submission
    this.convertBlobToBase64(blob)
  }

  async convertBlobToBase64(blob) {
    // Create a File from the Blob
    const file = new File([blob], "presentation.webm", { type: blob.type })
    
    // Create a DataTransfer to set the file input
    const dataTransfer = new DataTransfer()
    dataTransfer.items.add(file)
    
    // Find the file input and set its files
    const fileInput = this.element.querySelector('input[type="file"]')
    if (fileInput) {
      fileInput.files = dataTransfer.files
    }
  }

  handleFileSelect(event) {
    const file = event.target.files[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('video/')) {
      alert('Please select a video file')
      event.target.value = ''
      return
    }

    // Validate file size (50MB max)
    if (file.size > 50 * 1024 * 1024) {
      alert('Video file must be less than 50MB')
      event.target.value = ''
      return
    }

    // Show preview
    const videoURL = URL.createObjectURL(file)
    if (this.hasPreviewTarget) {
      this.previewTarget.src = videoURL
      this.previewTarget.muted = false
      this.previewTarget.controls = true
    }

    // Update UI
    if (this.hasPlaceholderTarget) {
      this.placeholderTarget.classList.add("hidden")
    }
    if (this.hasExistingVideoTarget) {
      this.existingVideoTarget.classList.add("hidden")
    }
    if (this.hasPreviewContainerTarget) {
      this.previewContainerTarget.classList.remove("hidden")
    }
      if (this.hasDeleteButtonContainerTarget) {
          this.deleteButtonContainerTarget.classList.remove("hidden")
      }
    if (this.hasRetakeButtonTarget) {
      this.retakeButtonTarget.classList.remove("hidden")
        this.retakeButtonTarget.classList.add("inline-flex")
    }
    if (this.hasRecordButtonTarget) {
      this.recordButtonTarget.classList.add("hidden")
        this.recordButtonTarget.classList.remove("inline-flex")
    }
    if (this.hasUploadButtonTarget) {
      this.uploadButtonTarget.classList.add("hidden")
        this.uploadButtonTarget.classList.remove("inline-flex")
    }
  }

  retake() {
    // Reset everything
    if (this.hasPreviewTarget) {
      this.previewTarget.src = ""
      this.previewTarget.srcObject = null
    }
    this.recordedChunks = []

    // Reset file input
    const fileInput = this.element.querySelector('input[type="file"]')
    if (fileInput) {
      fileInput.value = ''
    }

      // Update UI - show placeholder, record and upload buttons; hide preview, retake, and delete button
    if (this.hasPlaceholderTarget) {
      this.placeholderTarget.classList.remove("hidden")
    }
    if (this.hasPreviewContainerTarget) {
      this.previewContainerTarget.classList.add("hidden")
    }
    if (this.hasRetakeButtonTarget) {
      this.retakeButtonTarget.classList.add("hidden")
        this.retakeButtonTarget.classList.remove("inline-flex")
    }
      if (this.hasDeleteButtonContainerTarget) {
          this.deleteButtonContainerTarget.classList.add("hidden")
    }
    if (this.hasRecordButtonTarget) {
      this.recordButtonTarget.classList.remove("hidden")
        this.recordButtonTarget.classList.add("inline-flex")
    }
    if (this.hasUploadButtonTarget) {
      this.uploadButtonTarget.classList.remove("hidden")
        this.uploadButtonTarget.classList.add("inline-flex")
    }
  }

  startTimer() {
    this.recordingTime = 0
    this.updateTimerDisplay()

    this.timerInterval = setInterval(() => {
      this.recordingTime++
      this.updateTimerDisplay()

      // Auto-stop at max duration
      if (this.recordingTime >= this.maxDuration) {
        this.stopRecording()
      }
    }, 1000)
  }

  stopTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval)
      this.timerInterval = null
    }
  }

  updateTimerDisplay() {
    const minutes = Math.floor(this.recordingTime / 60).toString().padStart(2, '0')
    const seconds = (this.recordingTime % 60).toString().padStart(2, '0')
    if (this.hasTimerTarget) {
      this.timerTarget.textContent = `${minutes}:${seconds}`
    }
  }

  stopStream() {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop())
      this.stream = null
    }
  }

  cleanup() {
    this.stopTimer()
    this.stopStream()
    if (this.mediaRecorder && this.mediaRecorder.state === "recording") {
      this.mediaRecorder.stop()
    }
  }

  getSupportedMimeType() {
    const types = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
      'video/mp4'
    ]

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type
      }
    }

    return 'video/webm'
  }

  async startRecording() {
      // Check if in Flutter app and request camera/microphone permission first
      if (this.isInFlutterApp) {
          const hasCameraPermission = await this.requestFlutterPermission("Camera")
          if (!hasCameraPermission) {
              return // User denied camera permission
          }
          const hasMicPermission = await this.requestFlutterPermission("Microphone")
          if (!hasMicPermission) {
              return // User denied microphone permission
          }
      }

    try {
      // Request camera and microphone access
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 720 }, height: { ideal: 720 } },
        audio: true
      })

      // Show preview
      this.previewTarget.srcObject = this.stream
      this.previewTarget.muted = true // Mute to prevent feedback
      await this.previewTarget.play()

      // Hide placeholder and show preview
      if (this.hasPlaceholderTarget) {
        this.placeholderTarget.classList.add("hidden")
      }
      if (this.hasExistingVideoTarget) {
        this.existingVideoTarget.classList.add("hidden")
      }
      this.previewContainerTarget.classList.remove("hidden")

      // Setup MediaRecorder
      const options = { mimeType: this.getSupportedMimeType() }
      this.mediaRecorder = new MediaRecorder(this.stream, options)
      this.recordedChunks = []

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.recordedChunks.push(event.data)
        }
      }

      this.mediaRecorder.onstop = () => {
        this.handleRecordingComplete()
      }

      // Start recording
      this.mediaRecorder.start()

      // Update UI - hide record and upload buttons, show stop button
      if (this.hasRecordButtonTarget) {
        this.recordButtonTarget.classList.add("hidden")
          this.recordButtonTarget.classList.remove("inline-flex")
      }
      if (this.hasUploadButtonTarget) {
        this.uploadButtonTarget.classList.add("hidden")
          this.uploadButtonTarget.classList.remove("inline-flex")
      }
      if (this.hasStopButtonTarget) {
        this.stopButtonTarget.classList.remove("hidden")
          this.stopButtonTarget.classList.add("inline-flex")
      }
      if (this.hasRecordingIndicatorTarget) {
        this.recordingIndicatorTarget.classList.remove("hidden")
        this.recordingIndicatorTarget.classList.add("flex")
      }

      // Start timer
      this.startTimer()

    } catch (error) {
      console.error("Error starting recording:", error)
      alert("Unable to access camera. Please ensure camera permissions are granted.")
    }
  }

    /**
     * Request permission from Flutter
     * @param {string} permissionType - "Camera" or "Microphone"
     * @returns {Promise<boolean>} - true if granted, false otherwise
     */
    async requestFlutterPermission(permissionType) {
        try {
            const result = await window.flutter_inappwebview.callHandler(`request${permissionType}Permission`)
            return result?.granted || result === true
        } catch (error) {
            console.error(`Error requesting ${permissionType} permission:`, error)
            // Fall back to browser permission
            return true
        }
    }
}

